/**
 * Подсказка «что поставить» для каждой задачи: пять моделей OpenRouter,
 * дешёвых, но достаточных, от самой дешёвой к самой надёжной.
 *
 * Список — именно подсказка, а не ограничение: выбрать в админке можно любую
 * модель провайдера, и сервер принимает любой id. Смысл списка в том, что
 * каталог OpenRouter — это четыре с лишним сотни моделей, среди которых
 * подходящих задаче заметно меньше, а понять это по названию нельзя.
 *
 * ЖЁСТКИЙ ФИЛЬТР, общий для всех задач: сервер шлёт `response_format:
 * json_schema` со `strict: true` (server/ai/openrouter.mjs), поэтому модель
 * обязана поддерживать `structured_outputs`. Таких в каталоге примерно 340 из
 * 431 — модель без этой поддержки ответит ошибкой провайдера, а не текстом.
 * Дополнительно: скриншоту нужен вход `image`, ценам — `tools` (веб-поиск
 * подключается плагином `openrouter:web_search`), магии — `reasoning` и
 * контекст от 128K.
 *
 * Цены — доллары за 1M токенов на 2026-09-09, снято с /api/v1/models. Они
 * меняются, и список стареет: перед тем как ориентироваться на цифры,
 * стоит нажать «Обновить список» и сверить цену выбранной модели там.
 */

import type { AiSource, AiTuning } from './aiApi';

export interface RecommendedModel {
  id: string;
  /** $ за 1M входных токенов на дату снимка выше. */
  in: number;
  /** $ за 1M выходных токенов. */
  out: number;
}

/**
 * Скриншот: решает качество зрения, а не рассуждения — надо вынуть номер
 * рейса и время с картинки. Gemini Flash Lite стоит первым как самый
 * предсказуемый на мелком шрифте расписаний; обычный Flash — дороже вшестеро
 * по выходу и нужен, только если Lite путает цифры.
 */
const SCREENSHOT: readonly RecommendedModel[] = [
  { id: 'google/gemini-2.5-flash-lite', in: 0.1, out: 0.4 },
  { id: 'openai/gpt-5-nano', in: 0.05, out: 0.4 },
  { id: 'qwen/qwen3.5-flash-02-23', in: 0.065, out: 0.26 },
  { id: 'mistralai/mistral-small-3.2-24b-instruct', in: 0.075, out: 0.2 },
  { id: 'google/gemini-2.5-flash', in: 0.3, out: 2.5 },
];

/**
 * Ссылка: страницу открывает и очищает сервер, модель получает уже текст —
 * зрение не нужно. Тексты попадаются длинные, поэтому в списке модели с
 * запасом контекста, а не самые дешёвые из возможных.
 */
const URL_SOURCE: readonly RecommendedModel[] = [
  { id: 'openai/gpt-oss-20b', in: 0.03, out: 0.13 },
  { id: 'qwen/qwen3-30b-a3b-instruct-2507', in: 0.048, out: 0.193 },
  { id: 'deepseek/deepseek-v4-flash', in: 0.089, out: 0.177 },
  { id: 'google/gemini-2.5-flash-lite', in: 0.1, out: 0.4 },
  { id: 'openai/gpt-5-nano', in: 0.05, out: 0.4 },
];

/**
 * Текст: самая простая задача из всех — вынуть поля из пары строк. Здесь
 * дешёвого хватает буквально, и Mistral Nemo за две сотых доллара работает
 * не хуже моделей вдесятеро дороже.
 */
const TEXT: readonly RecommendedModel[] = [
  { id: 'mistralai/mistral-nemo', in: 0.019, out: 0.03 },
  { id: 'openai/gpt-oss-20b', in: 0.03, out: 0.13 },
  { id: 'qwen/qwen3-30b-a3b-instruct-2507', in: 0.048, out: 0.193 },
  { id: 'google/gemini-2.5-flash-lite', in: 0.1, out: 0.4 },
  { id: 'openai/gpt-5-nano', in: 0.05, out: 0.4 },
];

/**
 * Магия: единственная задача, где экономить опасно. Дешёвая модель путает
 * хронологию и выдумывает id блоков, сервер такие варианты отбрасывает, и
 * человек видит пустой результат вместо ошибки — то есть плата за неудачный
 * выбор здесь не «ответ хуже», а «ничего не работает».
 *
 * Считать стоит по выходу, а не по входу: ответ длинный (для магии сервер
 * поднимает лимит до 8000 токенов, и у reasoning-моделей туда же попадают
 * токены размышления). Поэтому DeepSeek V3.2 с выходом по 0.40 обходится
 * дешевле GPT-5 mini с его 2.00, хотя по входу они почти равны.
 */
const MAGIC: readonly RecommendedModel[] = [
  { id: 'deepseek/deepseek-v3.2', in: 0.269, out: 0.4 },
  { id: 'qwen/qwen3.5-35b-a3b', in: 0.312, out: 1.25 },
  { id: 'z-ai/glm-4.7', in: 0.4, out: 1.75 },
  { id: 'openai/gpt-5-mini', in: 0.25, out: 2.0 },
  { id: 'anthropic/claude-haiku-4.5', in: 1.0, out: 5.0 },
];

/**
 * Цены: токены здесь почти ничего не решают. Веб-поиск OpenRouter тарифицируется
 * отдельно от них и на порядок дороже самого запроса, поэтому выбирать надо не
 * самую дешёвую модель, а ту, что дисциплинированно ходит инструментом и
 * возвращает ссылку на источник: предложение без ссылки на открытый источник
 * сервер выбрасывает как непроверяемое (server/prices/providers/websearch.mjs),
 * и сэкономленные на модели центы просто уходят в пустой результат.
 */
const PRICES: readonly RecommendedModel[] = [
  { id: 'openai/gpt-5-nano', in: 0.05, out: 0.4 },
  { id: 'google/gemini-2.5-flash-lite', in: 0.1, out: 0.4 },
  { id: 'deepseek/deepseek-v3.2', in: 0.269, out: 0.4 },
  { id: 'openai/gpt-5-mini', in: 0.25, out: 2.0 },
  { id: 'google/gemini-2.5-flash', in: 0.3, out: 2.5 },
];

/** Дата снимка цен — показывается рядом со списком, чтобы возраст цифр был виден. */
export const RECOMMENDED_AS_OF = '2026-09-09';

/**
 * Record<AiSource, ...>, а не Record<string, ...>: новая задача на сервере
 * обязана получить свою подборку, иначе не соберётся. Без этого она молча
 * осталась бы без подсказки — ровно так `prices` однажды и остался без
 * карточки в панели.
 */
export const RECOMMENDED_MODELS: Record<AiSource, readonly RecommendedModel[]> = {
  screenshot: SCREENSHOT,
  url: URL_SOURCE,
  text: TEXT,
  magic: MAGIC,
  prices: PRICES,
};

/**
 * Рекомендуемые настройки задачи — то, что стоит выставить вместе с моделью.
 * Кнопка рядом со списком проставляет их в переопределения этой задачи.
 *
 * Общая логика: температура 0 везде, потому что все пять задач — извлечение
 * фактов и планирование, а не сочинение; воспроизводимость здесь важнее
 * разнообразия. Модели с неотключаемым рассуждением параметр игнорируют, и
 * это нормально.
 *
 * `dailyLimit` сюда не входит намеренно: сколько запросов в сутки давать
 * человеку — вопрос не модели, а того, сколько вы готовы платить, и угадать
 * это за администратора нельзя.
 */
export type RecommendedTuning = Omit<AiTuning, 'dailyLimit'>;

export const RECOMMENDED_TUNING: Record<AiSource, RecommendedTuning> = {
  /** Ответ короткий — поля одного рейса. Картинки декодируются небыстро, отсюда 45 секунд. */
  screenshot: { maxTokens: 2000, temperature: 0, timeoutSec: 45 },
  /** Плюс время на то, чтобы сервер сам открыл страницу до обращения к модели. */
  url: { maxTokens: 2000, temperature: 0, timeoutSec: 60 },
  /** Самая быстрая задача: короткий вход, короткий ответ. */
  text: { maxTokens: 2000, temperature: 0, timeoutSec: 30 },
  /**
   * Потолок токенов и времени: ответ длинный, а у reasoning-моделей в этот же
   * лимит попадают токены размышления — при меньшем значении ответ приезжает
   * обрезанным, и вариант молча теряется (admin.ai.req.tokens).
   */
  magic: { maxTokens: 8000, temperature: 0, timeoutSec: 180 },
  /** Веб-поиск ходит по сайтам до ответа, поэтому времени нужно больше обычного. */
  prices: { maxTokens: 4000, temperature: 0, timeoutSec: 120 },
};
